import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  CalendarBlank, CheckCircle, Circle, Clock, CurrencyEur, FileText, Gavel, Phone, EnvelopeSimple,
  Warning, ArrowRight, PencilSimple, ListChecks, Users, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ETAPES_CHANTIER, PHASES_CABINET } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * B1 — « Synthèse du dossier » : en tête du chantier, pour toute l'équipe, sur le modèle de
 * l'écran du client « Où en est-on ». Objectif : reprendre un dossier en moins de 2 minutes.
 * Une seule lecture : GET /api/chantiers/{id}/synthese (services/synthese_dossier_service.py).
 */

const eur = (n) => (n === null || n === undefined ? '—'
  : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} € HT`);
const dateFr = (v) => {
  if (!v) return '';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};
const dateHeure = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}${String(v).length > 10 ? ` à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}`;
};

function Bloc({ titre, icon: Icon, children, testid, action }) {
  return (
    <div className="card-kroquis" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-base font-semibold text-[#2C2C2C] flex items-center gap-2">
          {Icon && <Icon size={18} className="text-[#C75B39]" />} {titre}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Vide({ children }) {
  return <p className="text-sm text-[#9A9A9A]">{children}</p>;
}

function Lien({ onClick, children, testid }) {
  if (!onClick) return null;
  return (
    <button onClick={onClick} className="text-xs text-[#C75B39] hover:underline inline-flex items-center gap-1" data-testid={testid}>
      {children} <ArrowRight size={12} />
    </button>
  );
}

export function FriseEtapes({ frise, compact = false }) {
  return (
    <ol className="flex flex-wrap gap-1" data-testid="frise-etapes">
      {(frise || []).map((e) => (
        <li
          key={e.cle}
          className={`text-[11px] ${compact ? 'px-2 py-0.5' : 'px-2.5 py-1'} border flex items-center gap-1 ${
            e.etat === 'en_cours' ? 'bg-[#1E3A5F] text-white border-[#1E3A5F] font-semibold'
              : e.etat === 'fait' ? 'bg-[#F1F8E9] text-[#2D6A4F] border-[#C8E6C9]'
                : 'bg-white text-[#9A9A9A] border-[#E0E0DB]'}`}
          data-testid={`frise-${e.cle}`}
          aria-current={e.etat === 'en_cours' ? 'step' : undefined}
        >
          {e.etat === 'fait' && <CheckCircle size={12} weight="fill" />}
          {e.libelle}
        </li>
      ))}
    </ol>
  );
}

function CorrigerEtapeDialog({ open, onClose, chantierId, etape, onDone }) {
  const [form, setForm] = useState({ etape: etape?.cle, phase: etape?.phase || '', en_pause: !!etape?.en_pause, date_reception: etape?.date_reception || '' });
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    setForm({ etape: etape?.cle, phase: etape?.phase || '', en_pause: !!etape?.en_pause, date_reception: etape?.date_reception || '' });
  }, [etape, open]);
  const phases = ETAPES_CHANTIER.find((e) => e.cle === form.etape)?.phases || [];
  const enregistrer = async () => {
    setEnvoi(true);
    try {
      await axios.put(`${API_URL}/api/chantiers/${chantierId}/etape`, {
        etape: form.etape, phase: form.phase || null, en_pause: form.en_pause,
        date_reception: form.etape === 'gpa' && form.date_reception ? form.date_reception : null,
      }, { withCredentials: true });
      toast.success('Étape mise à jour');
      onDone?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : "Impossible de corriger l'étape");
    } finally { setEnvoi(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="corriger-etape-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Corriger l'étape</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#6B6B6B]">
          L'étape avance d'elle-même (consultation lancée, ordre de service, PV signé…). Corrigez-la ici si elle ne
          correspond pas à la réalité : elle ne sera plus remise en arrière par les événements passés.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Étape</Label>
            <Select value={form.etape} onValueChange={(v) => setForm({ ...form, etape: v, phase: '' })}>
              <SelectTrigger className="rounded-none mt-1" data-testid="corriger-etape-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ETAPES_CHANTIER.map((e) => <SelectItem key={e.cle} value={e.cle}>{e.libelle}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {phases.length > 0 && (
            <div>
              <Label className="text-xs">Phase (facultatif)</Label>
              <Select value={form.phase || 'aucune'} onValueChange={(v) => setForm({ ...form, phase: v === 'aucune' ? '' : v })}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucune">Non précisée</SelectItem>
                  {phases.map((p) => <SelectItem key={p} value={p}>{p} — {PHASES_CABINET[p] || p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.etape === 'gpa' && (
            <div>
              <Label className="text-xs">Date de réception (fin de la GPA un an après)</Label>
              <Input type="date" value={form.date_reception || ''} onChange={(e) => setForm({ ...form, date_reception: e.target.value })} className="rounded-none mt-1" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.en_pause} onChange={(e) => setForm({ ...form, en_pause: e.target.checked })} />
            Chantier en pause
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
            <Button className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" onClick={enregistrer} disabled={envoi} data-testid="corriger-etape-enregistrer">
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SyntheseDossier({ chantierId, onNavigateTab, onAction, refreshKey = 0, onLoaded, onChanged }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(false);
  const [corriger, setCorriger] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/chantiers/${chantierId}/synthese`, { withCredentials: true });
      setData(r.data);
      setErreur(false);
      onLoaded?.(r.data);
    } catch {
      setErreur(true);
    }
  }, [chantierId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { charger(); }, [charger, refreshKey]);

  if (erreur) return null;
  if (!data) {
    return <div className="card-kroquis h-40 animate-pulse bg-[#F5F5F3]" data-testid="synthese-chargement" />;
  }

  const { etape, echeances, prochaine_reunion: reunion, dernier_cr: cr, actions_en_retard: retards,
    argent, consultation, contacts, evenements, pour_bien_demarrer: demarrage } = data;
  const aller = (onglet) => (onNavigateTab ? () => onNavigateTab(onglet) : undefined);

  return (
    <section className="space-y-4" data-testid="synthese-dossier">
      {/* Où en est le dossier */}
      <div className="card-kroquis" data-testid="synthese-etape">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-[#6B6B6B]">Synthèse du dossier</p>
            <h2 className="text-2xl text-[#2C2C2C] mt-1" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Étape : {etape.libelle}
              {etape.phase && <span className="text-base text-[#6B6B6B]"> · {etape.phase} — {etape.phase_libelle}</span>}
            </h2>
            <p className="text-sm text-[#4A4A4A] mt-1">
              {etape.prospection && <span className="font-medium">Prospection · </span>}
              {etape.en_pause && <span className="font-medium text-[#D4880F]">En pause · </span>}
              {etape.a_faire}
            </p>
            {etape.suivante && (
              <p className="text-sm text-[#6B6B6B] mt-1" data-testid="synthese-prochaine-etape">
                Prochaine étape : <strong className="text-[#2C2C2C]">{etape.suivante.libelle}</strong>
              </p>
            )}
            {etape.gpa_fin && (
              <p className="text-sm text-[#2D6A4F] mt-1">Réception le {dateFr(etape.date_reception)} · garantie jusqu'au {dateFr(etape.gpa_fin)}</p>
            )}
          </div>
          {data.peut_corriger_etape && (
            <Button variant="outline" size="sm" className="rounded-none gap-1 text-xs self-start" onClick={() => setCorriger(true)} data-testid="corriger-etape-btn">
              <PencilSimple size={14} /> Corriger l'étape
            </Button>
          )}
        </div>
        <div className="mt-3"><FriseEtapes frise={etape.frise} /></div>
      </div>

      {/* Pour bien démarrer */}
      {demarrage && (
        <Bloc titre={`Pour bien démarrer (${demarrage.nb_faits}/${demarrage.total})`} icon={ListChecks} testid="synthese-demarrer">
          <ul className="space-y-1.5">
            {demarrage.items.map((it) => (
              <li key={it.cle} className="flex items-center gap-2 text-sm" data-testid={`demarrer-${it.cle}`}>
                {it.fait
                  ? <CheckCircle size={18} weight="fill" className="text-[#2D6A4F] flex-shrink-0" />
                  : <Circle size={18} className="text-[#9A9A9A] flex-shrink-0" />}
                <span className={it.fait ? 'text-[#6B6B6B] line-through' : 'text-[#2C2C2C]'}>{it.libelle}</span>
                {!it.fait && it.action && onAction && (
                  <Lien onClick={() => onAction(it.action)} testid={`demarrer-action-${it.cle}`}>Faire</Lien>
                )}
                {!it.fait && it.onglet && <Lien onClick={aller(it.onglet)}>Ouvrir</Lien>}
              </li>
            ))}
          </ul>
        </Bloc>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Échéances et réunion */}
        <Bloc titre="Prochaines échéances" icon={Clock} testid="synthese-echeances">
          {reunion ? (
            <div className="flex items-start gap-3 p-2 mb-3 bg-[#F5F5F3] border border-[#E0E0DB]" data-testid="synthese-prochaine-reunion">
              <CalendarBlank size={20} className="text-[#1E3A5F] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#2C2C2C]">{reunion.titre}</p>
                <p className="text-xs text-[#6B6B6B]">{dateHeure(reunion.date)}{reunion.lieu ? ` — ${reunion.lieu}` : ''}</p>
                <p className="text-[11px] text-[#6B6B6B]">{reunion.convoquee ? 'Convoquée' : 'Pas encore convoquée'}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#9A9A9A] mb-3">Aucune réunion planifiée.</p>
          )}
          {echeances.length === 0 ? <Vide>Aucune échéance à venir.</Vide> : (
            <ul className="space-y-2">
              {echeances.map((e, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="w-20 flex-shrink-0 text-xs font-semibold text-[#C75B39] pt-0.5">{dateFr(e.date)}</span>
                  <span className="min-w-0">
                    <span className="text-[#2C2C2C]">{e.libelle}</span>
                    {e.detail && <span className="text-[#6B6B6B]"> · {e.detail}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Bloc>

        {/* Dernier compte rendu */}
        <Bloc titre="Dernier compte rendu" icon={FileText} testid="synthese-dernier-cr"
          action={<Lien onClick={aller('suivi')}>Comptes rendus</Lien>}>
          {!cr ? <Vide>Aucun compte rendu diffusé pour l'instant.</Vide> : (
            <div className="space-y-2">
              <p className="text-sm text-[#2C2C2C]">
                <strong>CR n° {cr.numero ?? '—'}</strong>
                {cr.date && <span className="text-[#6B6B6B]"> · réunion du {dateFr(cr.date)}</span>}
                {cr.diffuse_le && <span className="text-[#6B6B6B]"> · diffusé le {dateFr(cr.diffuse_le)}</span>}
              </p>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#6B6B6B] flex items-center gap-1"><Gavel size={12} /> Décisions</p>
                {cr.decisions.length === 0 ? <Vide>Aucune décision notée.</Vide> : (
                  <ul className="list-disc pl-5 text-sm text-[#2C2C2C]" data-testid="synthese-decisions">
                    {cr.decisions.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                )}
              </div>
              {cr.desaccords.length > 0 && (
                <div className="p-2 bg-[#FFF8E1] border border-[#FFE082]" data-testid="synthese-desaccords">
                  <p className="text-xs font-semibold text-[#8D6E00] flex items-center gap-1"><Warning size={12} /> Désaccords des entreprises</p>
                  <ul className="text-sm text-[#2C2C2C] mt-1 space-y-1">
                    {cr.desaccords.map((d, i) => (
                      <li key={i}><strong>{d.entreprise}</strong>{d.point ? ` sur « ${d.point} »` : ''}{d.commentaire ? ` : ${d.commentaire}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Bloc>

        {/* Actions en retard */}
        <Bloc titre="Actions en retard" icon={Warning} testid="synthese-retards"
          action={<Lien onClick={aller('taches')}>Toutes les actions</Lien>}>
          {retards.total === 0 ? <Vide>Aucune action en retard.</Vide> : (
            <ul className="space-y-2">
              {retards.par_entreprise.map((g) => (
                <li key={g.entreprise} className="text-sm">
                  <p className="text-[#2C2C2C]">
                    <strong>{g.entreprise}</strong> : {g.nb} action{g.nb > 1 ? 's' : ''} en retard
                    <span className="text-[#C53030]"> (jusqu'à {g.plus_ancien_jours} j)</span>
                  </p>
                  <ul className="pl-4 text-xs text-[#6B6B6B]">
                    {g.actions.map((a) => <li key={a.id}>{a.titre} — prévu le {dateFr(a.echeance)}</li>)}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </Bloc>

        {/* Argent (P4#15) — seulement si la personne voit les montants */}
        {argent && (
          <Bloc titre="Argent" icon={CurrencyEur} testid="synthese-argent"
            action={<Lien onClick={aller('finances')}>Finances</Lien>}>
            {argent.rien ? <Vide>Rien à certifier, rien en attente, rien à payer.</Vide> : (
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between gap-2">
                  <span>À certifier{argent.a_certifier.plus_ancienne ? ` (déposée le ${dateFr(argent.a_certifier.plus_ancienne)})` : ''}</span>
                  <span className="font-medium whitespace-nowrap">{argent.a_certifier.nb} situation{argent.a_certifier.nb > 1 ? 's' : ''} · {eur(argent.a_certifier.montant_ht)}</span>
                </li>
                <li className="flex justify-between gap-2">
                  <span>En attente du client{argent.attente_client.depuis ? ` (depuis le ${dateFr(argent.attente_client.depuis)})` : ''}</span>
                  <span className="font-medium whitespace-nowrap">{argent.attente_client.nb} avenant{argent.attente_client.nb > 1 ? 's' : ''} · {eur(argent.attente_client.montant_ht)}</span>
                </li>
                <li className="flex justify-between gap-2">
                  <span>
                    À payer par le client
                    {argent.a_payer.nb_en_retard > 0 && <span className="text-[#C53030]"> — {argent.a_payer.nb_en_retard} échéance{argent.a_payer.nb_en_retard > 1 ? 's' : ''} dépassée{argent.a_payer.nb_en_retard > 1 ? 's' : ''}</span>}
                  </span>
                  <span className="font-medium whitespace-nowrap">{argent.a_payer.nb} situation{argent.a_payer.nb > 1 ? 's' : ''} · {eur(argent.a_payer.montant_ht)}</span>
                </li>
              </ul>
            )}
          </Bloc>
        )}

        {/* Consultation en cours */}
        {consultation && (
          <Bloc titre="Consultation en cours" icon={Gavel} testid="synthese-consultation"
            action={<Lien onClick={aller('marches')}>Marchés</Lien>}>
            <p className="text-sm text-[#2C2C2C]">
              {consultation.statut === 'en_preparation' ? 'En préparation' : 'Lancée'}
              {consultation.date_limite && <> · date limite le <strong>{dateFr(consultation.date_limite)}</strong>{consultation.depassee ? ' (dépassée)' : ''}</>}
            </p>
            <p className="text-sm text-[#6B6B6B]">
              {consultation.nb_entreprises} entreprise{consultation.nb_entreprises > 1 ? 's' : ''} consultée{consultation.nb_entreprises > 1 ? 's' : ''} · {consultation.nb_offres} offre{consultation.nb_offres > 1 ? 's' : ''} reçue{consultation.nb_offres > 1 ? 's' : ''} · reste à attribuer : {consultation.reste_a_attribuer}
            </p>
            <ul className="mt-2 space-y-1 text-xs">
              {consultation.lots.map((l) => (
                <li key={l.id} className="flex justify-between gap-2">
                  <span className="text-[#2C2C2C]">{l.libelle}</span>
                  <span className={l.attribue ? 'text-[#2D6A4F]' : 'text-[#6B6B6B]'}>
                    {l.attribue ? 'Attribué' : `${l.nb_offres} offre${l.nb_offres > 1 ? 's' : ''} / ${l.nb_invitees} invitée${l.nb_invitees > 1 ? 's' : ''}`}
                  </span>
                </li>
              ))}
            </ul>
          </Bloc>
        )}

        {/* Contacts */}
        <Bloc titre="Contacts" icon={Users} testid="synthese-contacts">
          <div className="space-y-3 text-sm">
            {[['Équipe', contacts.equipe], ['Client', contacts.clients], ['Entreprises titulaires', contacts.entreprises]].map(([titre, liste]) => (
              <div key={titre}>
                <p className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1">{titre}</p>
                {(!liste || liste.length === 0) ? <Vide>—</Vide> : (
                  <ul className="space-y-1">
                    {liste.map((p, i) => (
                      <li key={i} className="flex flex-wrap items-center gap-x-2">
                        <span className="font-medium text-[#2C2C2C]">{p.nom}</span>
                        {(p.fonction || (p.lots && p.lots.length > 0)) && (
                          <span className="text-xs text-[#6B6B6B]">{p.fonction || p.lots.join(', ')}</span>
                        )}
                        {p.acces === false && <span className="text-[10px] text-[#9A9A9A] border border-[#E0E0DB] px-1">sans accès</span>}
                        {p.telephone && (
                          <a href={`tel:${p.telephone}`} className="text-xs text-[#1E3A5F] inline-flex items-center gap-0.5 hover:underline"><Phone size={12} />{p.telephone}</a>
                        )}
                        {p.email && (
                          <a href={`mailto:${p.email}`} className="text-xs text-[#1E3A5F] inline-flex items-center gap-0.5 hover:underline break-all"><EnvelopeSimple size={12} />{p.email}</a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Bloc>

        {/* Historique */}
        <Bloc titre="Derniers événements" icon={ClockCounterClockwise} testid="synthese-evenements">
          {evenements.length === 0 ? <Vide>Aucun événement.</Vide> : (
            <ul className="space-y-1.5">
              {evenements.map((ev) => (
                <li key={ev.id} className="text-sm">
                  <span className="text-xs text-[#9A9A9A] mr-2">{dateFr(ev.timestamp)}</span>
                  <span className="text-[#2C2C2C]">{ev.action}</span>
                  {ev.details && <span className="text-[#6B6B6B]"> — {ev.details}</span>}
                  {ev.user_name && ev.user_name !== 'Kroquis' && <span className="text-xs text-[#9A9A9A]"> · {ev.user_name}</span>}
                </li>
              ))}
            </ul>
          )}
        </Bloc>
      </div>

      <CorrigerEtapeDialog open={corriger} onClose={() => setCorriger(false)} chantierId={chantierId} etape={etape} onDone={() => { charger(); onChanged?.(); }} />
    </section>
  );
}
